import { useEffect, useState } from 'react';
import { getAlertasMonitoreo } from '../services/monitoreoService';
import { PlantaFechas, PlantaFechaFilterProps } from '../components/others/Filters/PlantaFechas';
import { dateToInput } from '../utils/utility';

const categorias = { Alta: 'danger', Media: 'warning', Baja: 'success' };

interface Alerta {
  fecha: string;
  cupo: string;
  tipo: string;
  descripcion: string;
  nivel: 'Alta' | 'Media' | 'Baja';
  promedio: string;
  actual: string;
  variacion: string;
  historial?: { fecha: string; litros: number }[];
}

export const AlertasMonitoreo = () => {
  const [filterData, setFilterData] = useState<Omit<PlantaFechaFilterProps, 'tina' | 'intervalo' | 'fechaIni'>>({
    planta: 0,
    ruta: 0,
    cupo: 0,
    fechaFin: dateToInput(),
  });

  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [loading, setLoading] = useState(false);
  const [alertaSeleccionada, setAlertaSeleccionada] = useState<Alerta | null>(null);
  const [modoManual, setModoManual] = useState(false);
  const [colapsado, setColapsado] = useState<{ [key: string]: boolean }>({ Alta: false, Media: false, Baja: false });

  const handleSubmit = async (dataFilter: any) => {
    setLoading(true);
    try {
      const result = await getAlertasMonitoreo({
        planta: dataFilter.planta,
        ruta: dataFilter.ruta,
        cupo: dataFilter.cupo,
        fecha: dataFilter.fechaFin || dateToInput(),
      });
      setAlertas(result || []);
      setFilterData(dataFilter);
      setModoManual(true);
    } catch (err) {
      console.error('Error al obtener alertas', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fechaHoy = dateToInput();
    const autoLoad = async () => {
      try {
        const result = await getAlertasMonitoreo({ planta: 0, ruta: 0, cupo: 0, fecha: fechaHoy });
        setAlertas(result || []);
      } catch (error) {
        console.error('Error cargando alertas automáticas', error);
      }
    };

    autoLoad();
    const interval = setInterval(() => {
      if (!modoManual) autoLoad();
    }, 180000);
    return () => clearInterval(interval);
  }, [modoManual]);

  const alertasPorNivel = (nivel: 'Alta' | 'Media' | 'Baja') =>
    alertas.filter((a) => a.nivel === nivel);

  return (
    <div className="container py-4">
      <h4 className="text-center pb-4">Alertas de monitoreo</h4>
      <div className="d-flex justify-content-center mb-4">
        <PlantaFechas runSearch={handleSubmit} initialValuesFilter={filterData} hideTina hideIntervalo hideFechaIni />
      </div>

      {loading ? (
        <p className="text-center">Cargando alertas...</p>
      ) : (
        ['Alta', 'Media', 'Baja'].map((nivel) => (
          <div key={nivel} className="mb-4">
            <div
              className={`d-flex justify-content-between align-items-center p-3 rounded border border-${categorias[nivel as keyof typeof categorias]}`}
              style={{ backgroundColor: '#f8f9fa', cursor: 'pointer' }}
              onClick={() => setColapsado({ ...colapsado, [nivel]: !colapsado[nivel] })}
            >
              <h5 className={`fw-bold m-0 text-${categorias[nivel as keyof typeof categorias]}`}>
                {nivel}s ({alertasPorNivel(nivel as 'Alta' | 'Media' | 'Baja').length})
              </h5>
              <span>{colapsado[nivel] ? '▼' : '▲'}</span>
            </div>

            {!colapsado[nivel] && (
              <ul className="list-group list-group-flush mt-2" style={{ backgroundColor: '#f8f9fa' }}>
                {alertasPorNivel(nivel as 'Alta' | 'Media' | 'Baja').length === 0 ? (
                  <li className="list-group-item text-center border-0 bg-transparent">No hay alertas</li>
                ) : (
                  alertasPorNivel(nivel as 'Alta' | 'Media' | 'Baja').map((alerta, i) => (
                    <li
                      key={i}
                      className="list-group-item d-flex justify-content-between align-items-center rounded mb-2 shadow-sm"
                      style={{
                        borderLeft: `4px solid ${
                          nivel === 'Alta' ? '#dc3545' : nivel === 'Media' ? '#ffc107' : '#198754'
                        }`,
                        background: '#ffffff',
                        cursor: 'pointer',
                      }}
                      onClick={() => setAlertaSeleccionada(alerta)}
                    >
                      <div>
                        <div className="fw-bold">{alerta.tipo}</div>
                        <div className="small mb-1">
                          <span className="badge bg-secondary me-2">Cupo {alerta.cupo}</span>
                          {alerta.descripcion}
                        </div>
                      </div>
                      <small>{alerta.fecha}</small>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        ))
      )}

      {alertaSeleccionada && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detalles de la alerta</h5>
                <button type="button" className="btn-close" onClick={() => setAlertaSeleccionada(null)}></button>
              </div>
              <div className="modal-body">
                <p><strong>Cupo:</strong> {alertaSeleccionada.cupo}</p>
                <p><strong>Tipo:</strong> {alertaSeleccionada.tipo}</p>

                {alertaSeleccionada.promedio !== '-' && (
                  <>
                    <p><strong>Promedio 15 días:</strong> {alertaSeleccionada.promedio} L</p>
                    <p><strong>Valor actual:</strong> {alertaSeleccionada.actual} L</p>
                    <p><strong>Variación:</strong> {alertaSeleccionada.variacion}%</p>
                  </>
                )}

                <p><strong>Fecha:</strong> {alertaSeleccionada.fecha}</p>
                {alertaSeleccionada.descripcion && <p><strong>Detalle:</strong> {alertaSeleccionada.descripcion}</p>}

                {alertaSeleccionada.historial && (
                  <>
                    <hr />
                    <h6>Historial de días anteriores:</h6>
                    <ul className="list-unstyled ms-3">
                      {alertaSeleccionada.historial.map((h, i) => (
                        <li key={i}>• {h.fecha} → <strong>{h.litros} litros</strong></li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setAlertaSeleccionada(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


